import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Contact() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dob: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('dob', formData.dob);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6on83uancq2eie821ag', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        if (typeof (window as any).gtag === 'function') {
          (window as any).gtag('event', 'ads_conversion_Contact_Us_1', {
            'event_timeout': 2000,
          });
          (window as any).gtag('event', 'conversion', {
            'send_to': 'AW-17958081234/EAShCLrro4kcENKlivNC'
          });
        }
        navigate('/thank-you');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-stone-800 to-stone-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-violet-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-violet-600 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <span className="text-violet-400 font-semibold text-sm uppercase tracking-wide">Ready To Start?</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-4">
            Take The First Step<br />Toward Clarity
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Fill out the form below. We will provide you access to our confidential patient portal to schedule your testing.
          </p>
        </div>

        <form 
          id="contact-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 md:p-10 border border-white/20"
        >
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="name" className="block text-white font-semibold mb-2 text-sm">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-white/90 border border-white/30 focus:outline-none focus:ring-2 focus:ring-violet-500 text-gray-900 text-sm"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-white font-semibold mb-2 text-sm">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-white/90 border border-white/30 focus:outline-none focus:ring-2 focus:ring-violet-500 text-gray-900 text-sm"
                placeholder="john@example.com"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="phone" className="block text-white font-semibold mb-2 text-sm">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-white/90 border border-white/30 focus:outline-none focus:ring-2 focus:ring-violet-500 text-gray-900 text-sm"
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <label htmlFor="dob" className="block text-white font-semibold mb-2 text-sm">Date of Birth *</label>
              <input
                type="date"
                id="dob"
                name="dob"
                required
                value={formData.dob}
                onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-white/90 border border-white/30 focus:outline-none focus:ring-2 focus:ring-violet-500 text-gray-900 text-sm"
              />
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="message" className="block text-white font-semibold mb-2 text-sm">Message</label>
            <textarea
              id="message"
              name="message"
              rows={5}
              maxLength={500}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-4 py-3 rounded-lg bg-white/90 border border-white/30 focus:outline-none focus:ring-2 focus:ring-violet-500 text-gray-900 resize-none text-sm"
              placeholder="Tell us about your symptoms or ask any questions..."
            ></textarea>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-violet-600 text-white py-4 rounded-full text-base font-semibold hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSubmitting ? 'Sending...' : 'Send Message →'}
          </button>

          {submitStatus === 'error' && (
            <div className="mt-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
              <p className="text-white text-sm text-center">Something went wrong. Please try again.</p>
            </div>
          )}
        </form>

        <div className="mt-12 flex flex-wrap justify-center gap-8 text-white/80">
          <div className="flex items-center gap-3">
            <i className="ri-shield-check-line text-2xl text-violet-400"></i>
            <span className="text-sm font-medium">HIPAA Compliant</span>
          </div>
          <div className="flex items-center gap-3">
            <i className="ri-time-line text-2xl text-violet-400"></i>
            <span className="text-sm font-medium">48-Hour Results</span>
          </div>
        </div>
      </div>
    </section>
  );
}
